import { withChildren, BuilderBlocks, type BuilderElement } from '@builder.io/react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Text, Button, ButtonProps } from '@videoblocks/design-system';

export interface FooterHookProps {
    headline: string;
    content: string;
    showCallToAction: boolean;
    callToAction: ButtonProps & { text: string };
    shadowVariant: boolean;
    backgroundColor?: string;
    shadowOptions?:
        | {
              shadowColor: string;
              shadowImage: string;
          }
        | undefined;
    dynamicContent: {
        blocks: React.ReactNode[];
    };
    builderBlock: BuilderElement;
}

const FooterHook = ({
    headline,
    showCallToAction,
    callToAction,
    shadowVariant,
    shadowOptions,
    backgroundColor,
    dynamicContent = {
        blocks: [],
    },
    builderBlock,
}: FooterHookProps) => {
    const { shadowColor, shadowImage } = shadowOptions || {};
    const contentClass = twMerge('relative', shadowVariant && 'w-[calc(100%-2rem)] left-6');
    const { blocks } = dynamicContent;
    return (
        <div className="px-6 py-16 md:px-12 md:py-24 lg:p-24">
            <div className={contentClass}>
                {shadowVariant ? (
                    <div className="absolute -inset-0">
                        <div
                            className="absolute w-full h-full -left-6 top-6 rounded-xl"
                            style={{
                                backgroundColor: shadowColor || undefined,
                                backgroundImage: shadowImage ? `url(${shadowImage})` : undefined,
                                backgroundSize: shadowImage && 'cover',
                            }}
                            role="presentation"
                        />
                    </div>
                ) : null}
                <div
                    className="relative bg-blue-200 px-6 py-12 md:p-12 md:gap-4 lg:p-24 rounded-xl"
                    style={{ backgroundColor }}
                >
                    <div className="flex flex-col gap-4 lg:gap-8 lg:flex-row">
                        <Text
                            fontFamily="polysans"
                            fontSize="4xl"
                            fontWeight="semibold"
                            className="flex-1 text-gray-900"
                        >
                            {headline}
                        </Text>
                        <div className="flex-1">
                            <BuilderBlocks
                                parentElementId={builderBlock.id}
                                dataPath={`dynamicContent.blocks`}
                                blocks={blocks}
                            />
                            {showCallToAction ? (
                                <Button
                                    href={callToAction.href}
                                    variant={callToAction.variant}
                                    target={callToAction.target}
                                    className="mt-8"
                                    size="large"
                                >
                                    {callToAction.text}
                                </Button>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withChildren(FooterHook);
